'use client'
import 'katex/dist/katex.min.css'
import React, { useEffect, useRef, useState } from 'react'
import Keyboard from 'react-simple-keyboard'
import dynamic from 'next/dynamic'
import 'react-simple-keyboard/build/css/index.css'
import useAlgebraButtonTheme from './button-themes/useAlgebraButtonTheme'
import useAlphabeticNumericButtonTheme from './button-themes/useAlphabeticNumericButtonTheme'
import useGeometricButtonTheme from './button-themes/useGeometricButtonTheme'
import useIntegerationButtonTheme from './button-themes/useIntegerationButtonTheme'
import AlphabeticKeyboard from './default-keyboard'
import {
  useKeyboardHook,
  useNumericKeyboardHook,
  useSymbolicKeyboardHook,
} from './hooks/layoutHooks'
import useButtonThemeForKeyboard from './hooks/useButtonThemeForKeyboard'
import { allKeysProps } from './keys/keys'
import NumericKeyboard from './numeric-keyboard'
import SymbolicKeyboard from './symbolic-keyboard'
import send from '@/images/ai-agent/send-message.svg'
import Image from 'next/image'
import { Tooltip } from 'react-tooltip'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

const MathQuill = dynamic(
  () => import('@/components/page-partials/math-quill'),
  {
    ssr: false,
  },
)

const MathEditorLayout = ({
  setEquation,
  hideInput,
  inputClassName,
  readOnly,
  initialEquation,
  submitOnKeyDown,
  isKeyboardShown,
  showKeyboardToggleIcon = false,
  handleToggleKeyboard = () => {},
  additionalKeyboardWrapperClassName = '',
  isOnboardingFlow = false,
  handleAnswer = () => {},
  bottomControls,
  submitBtnElement,
}) => {
  // custom hooks
  const [keyboardLayout, setKeyboardLayout, keyboardDisplay] = useKeyboardHook()
  const [numericLayout, setNumericLayout] = useNumericKeyboardHook()
  const [symbolLayout, setSymbolLayout] = useSymbolicKeyboardHook()

  // themes
  const { defaultButtonTheme } = useAlphabeticNumericButtonTheme()
  const { integerationButtonTheme } = useIntegerationButtonTheme()
  const { algebraButtonTheme } = useAlgebraButtonTheme()
  const { geometricButtonTheme } = useGeometricButtonTheme()

  // states
  const [buttonTheme, setButtonTheme] = useButtonThemeForKeyboard()
  const [nameLayout, setNameLayout] = useState('default')
  const [content, setContent] = useState('')
  const [keyboardOpen, setKeyboardOpen] = useState(false)
  const keyboard = useRef()
  const mf = useRef()

  // States
  const [defaultKeyboard, setDefaultKeyboard] = useState(false)
  const [capsLock, setCapsLock] = useState(false)
  const [geometricLock, setGeometricLock] = useState(false)
  const [integerationLock, setIntegerationLock] = useState(false)
  const [algebaricLock, setAlgebaricLock] = useState(false)
  const [KeysPropsMap, setKeysPropsMap] = useState()
  const { imageUrl, loading } = useSelector(({ imageSession }) => imageSession)

  // Effects
  useEffect(() => {
    setKeysPropsMap(
      new Map(
        allKeysProps.map((obj) => {
          return [obj.id, obj]
        }),
      ),
    )
  }, [])

  useEffect(() => {
    if (capsLock) {
      setNameLayout('shift')
      setButtonTheme(defaultButtonTheme)
    } else {
      setNameLayout('default')
      setButtonTheme(defaultButtonTheme)
    }
  }, [capsLock])

  useEffect(() => {
    if (algebaricLock) {
      setNameLayout('algebraicEquations')
      setButtonTheme(algebraButtonTheme)
    } else {
      setNameLayout('default')
      setButtonTheme(defaultButtonTheme)
    }
  }, [algebaricLock])

  useEffect(() => {
    if (geometricLock) {
      setNameLayout('geometricEquations')
      setButtonTheme(geometricButtonTheme)
    } else {
      setNameLayout('default')
      setButtonTheme(defaultButtonTheme)
    }
  }, [geometricLock])

  useEffect(() => {
    if (defaultKeyboard) {
      setNameLayout('default')
      setButtonTheme(defaultButtonTheme)
    }
  }, [defaultKeyboard])

  useEffect(() => {
    if (integerationLock) {
      setNameLayout('integrationEquations')
      setButtonTheme(integerationButtonTheme)
    } else {
      setNameLayout('default')
      setButtonTheme(defaultButtonTheme)
    }
  }, [integerationLock])

  const workOnKeyPress = (keyPressed) => {
    let key = KeysPropsMap.get(keyPressed)

    if (keyPressed === 's4') {
      key.lable = '{}\\frac{}{}'
      key.mathfieldInstructions.content = '{}\\frac{}{}'
    }
    if (keyPressed === 'space') {
      key.lable = '\\\\quad'
      key.mathfieldInstructions.content = '{}\\ {}{}'
    }
    mf.current[key.mathfieldInstructions.method](
      key.mathfieldInstructions.content,
    )
    if (
      keyPressed === '(□)' ||
      keyPressed === 's6' ||
      keyPressed === '&#8730' ||
      keyPressed === 's2' ||
      keyPressed === 'a1' ||
      keyPressed === 'a2' ||
      keyPressed === 'a6' ||
      keyPressed === 'a9' ||
      keyPressed === 'a7' ||
      keyPressed === 'a9' ||
      keyPressed === 'I1' ||
      keyPressed === 's1'
    ) {
      workOnKeyPress('{arrowleft}')
    }
    if (keyPressed === 's3' || keyPressed === 'a3' || keyPressed === 'a4') {
      workOnKeyPress('{arrowup}')
    }
    if (keyPressed === 'I20' || keyPressed === 'a10') {
      for (let i = 0; i < 5; i++) {
        workOnKeyPress('{arrowleft}')
      }
    }
    if (keyPressed === 'a11') {
      for (let i = 0; i < 2; i++) {
        workOnKeyPress('{arrowleft}')
      }
    }
    if (keyPressed === 's4') {
      for (let i = 0; i < 3; i++) {
        workOnKeyPress('{arrowleft}')
      }
    }
  }

  const onKeyPressAlphabetic = (button) => {
    if (button === '{capslock}') {
      // shift
      setCapsLock(!capsLock)
    } else if (button === '&#946') {
      // geometric
      setGeometricLock(!geometricLock)
    } else if (button === 'abc') {
      // default
      // reset all setting
      setCapsLock(false)
      setGeometricLock(false)
      setIntegerationLock(false)
      setAlgebaricLock(false)

      // switch to default
      setDefaultKeyboard(true)
    } else if (button === '&#x222B') {
      // integral
      setIntegerationLock(!integerationLock)
    } else if (button === '&#8730') {
      // algebaric
      setAlgebaricLock(!algebaricLock)
    } else if (button === 'arrowup') {
      keyboard.current.setCaretPosition(1)
    } else if (button === 'space') {
      workOnKeyPress(button)
    } else {
      workOnKeyPress(button)
    }
  }

  useEffect(() => {
    if (setEquation) {
      setEquation(content.replace('\\iint_', '\\int_') || initialEquation)
    }
  }, [content])

  const onKeyPressNumeric = (button) => {
    if (button === 'arrowup') {
      keyboard.current.setCaretPosition(1)
    } else {
      workOnKeyPress(button)
    }
  }

  const onKeyPressSymbolic = (button) => {
    workOnKeyPress(button)
  }

  const onChangeAlphabetic = (input) => {}

  const onChangeNumeric = (input) => {}

  const onChangeSymbolic = (input) => {}

  return (
    <div className="w-full mx-auto relative">
      {typeof window !== 'undefined' && (
        <MathQuill
          latex={content}
          setLatex={setContent}
          keyboardOpen={keyboardOpen}
          setKeyboardOpen={setKeyboardOpen}
          keyboardRef={mf}
          hideInput={hideInput}
          inputClassName={inputClassName}
          readOnly={readOnly}
          initialEquation={initialEquation}
          setEquation={setEquation}
          submitOnKeyDown={submitOnKeyDown}
          showKeyboardToggleIcon={showKeyboardToggleIcon}
          handleToggleKeyboard={handleToggleKeyboard}
          isKeyboardShown={isKeyboardShown}
          submitBtnElement={submitBtnElement}
        >
          {!!bottomControls && bottomControls}

          {isKeyboardShown && (
            <div
              className={`${
                additionalKeyboardWrapperClassName
                  ? additionalKeyboardWrapperClassName
                  : 'grid grid-cols-5 w-[860px] bg-white mx-auto mt-[1rem]'
              }`}
            >
              <AlphabeticKeyboard
                keyboard={keyboard}
                layoutDesign={keyboardLayout}
                display={keyboardDisplay}
                nameLayout={nameLayout}
                onKeyPress={onKeyPressAlphabetic}
                onChange={onChangeAlphabetic}
                buttonTheme={buttonTheme}
              />
              <NumericKeyboard
                keyboard={Keyboard}
                display={keyboardDisplay}
                numericLayout={numericLayout}
                onKeyPress={onKeyPressNumeric}
                onChange={onChangeNumeric}
              />
              <SymbolicKeyboard
                keyboard={Keyboard}
                display={keyboardDisplay}
                symbolLayout={symbolLayout}
                onChange={onChangeSymbolic}
                onKeyPress={onKeyPressSymbolic}
              />
            </div>
          )}

          {isOnboardingFlow && (
            <div
              className={`absolute right-9 ${
                imageUrl || loading ? 'top-[176px]' : 'top-28'
              } py-1 px-2 cursor-pointer`}
              onClick={handleAnswer}
              data-tooltip-id="send-onboarding-message"
              data-tooltip-content="Send message"
              data-tooltip-place="top"
            >
              <Image src={send} alt="send-message" width={20} height={20} />
              <Tooltip id="send-onboarding-message" />
            </div>
          )}
        </MathQuill>
      )}
    </div>
  )
}

MathEditorLayout.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  inputClassName: PropTypes.string,
  setEquation: PropTypes.func.isRequired,
  hideInput: PropTypes.bool,
  initialEquation: PropTypes.string,
  isKeyboardShown: PropTypes.bool.isRequired,
  showKeyboardToggleIcon: PropTypes.bool,
  additionalKeyboardWrapperClassName: PropTypes.string,
  handleToggleKeyboard: PropTypes.func.isRequired,
  submitOnKeyDown: PropTypes.func.isRequired,
  bottomControls: PropTypes.node,
  submitBtnElement: PropTypes.node,
}

export default MathEditorLayout
